import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'
import { debounce, getElement, getElements, translateString } from '@bao/utils'

const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = getElements(`[${this.element._elIdentifier}="item"]`, {
      context: html,
    })

    newElements.forEach((el) => newItems.appendChild(el))

    if (direction == null) {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.element.els['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      ?.setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      ?.setAttribute('aria-hidden', (newElements.length > 0).toString())
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.element.els['load-previous'].selector}, ${this.element.els['load-more'].selector}`,
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML = getElement(
        this.element.els['load-previous'].selector,
        { context: html },
      ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML = getElement(
        this.element.els['load-more'].selector,
        {
          context: html,
        },
      ).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = getElements(`${this.element.els.item.selector}`)

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = getElements(`${this.element.els.item.selector}`)

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)
    this.renderProductCount(newHTMLDom)
    this.renderFilterDrawerTrigger(newHTMLDom)
    this.renderFauxFacets(newHTMLDom)
    this.renderAdditional(newHTMLDom)

    this.element.loading = false
  }

  restoreFocus () {
    const restoreId = this.element.dataset.restoreFocusId
    if (!restoreId) return

    const newInput = this.element.querySelector(`#${CSS.escape(restoreId)}`)

    if (newInput) {
      newInput.focus()
    }

    delete this.element.dataset.restoreFocusId
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderFacets (document) {
    const getFacetSelector = (facet) =>
      `${this.element.els.facet.selector}[data-index="${facet.dataset.index}"]`

    const currentFacetElements = this.element.els.facet.elements

    this.element.log('renderFacets', {
      currentFacetElements,
    })

    currentFacetElements.forEach((facet) => {
      const selector = getFacetSelector(facet)
      const el = getElement(selector, { context: document })
      const facetItem = facet.closest(
        '[data-collection-filters-el="facet-item"]',
      )

      if (!el) {
        if (facetItem) {
          facetItem.hidden = true
        } else {
          facet.innerHTML = ''
        }

        return
      }

      if (facetItem) facetItem.hidden = false

      facet.innerHTML = el.innerHTML
    })

    this.restoreFocus()
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.els['active-facets']?.elements
    if (!activeFacets?.length) return

    const newActiveFacets = getElements(
      this.element.els['active-facets'].selector,
      {
        context: document,
      },
    )

    activeFacets.forEach((activeFacet, index) => {
      const activeFacetElement = newActiveFacets[index]
      if (!activeFacetElement) return

      const hasActiveItems = !!getElement(
        `[${this.element._elIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()
      activeFacet.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount (document) {
    const newProductGrid = getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    )

    if (!newProductGrid) return

    const productCounts = getElements(
      this.element.els['product-count']?.selector,
      { context: this.element },
    )

    const productItems = getElements('[data-product-item]', {
      context: this.element,
    })

    const shownCount = productItems.length
    const totalCount = Number(newProductGrid.dataset.totalCount)
    const type = window.theme.strings.pagination.products

    const productCountTranslation =
      totalCount === 1
        ? window.theme.strings.collection.productCountOne
        : window.theme.strings.collection.productCountOther

    productCounts.forEach((element) => {
      if (element.dataset.countType === 'collection') {
        element.innerHTML = translateString(productCountTranslation, {
          product_count: shownCount,
          count: totalCount,
        })

        return
      }

      if (element.dataset.countType === 'pagination') {
        element.innerHTML = translateString(
          window.theme.strings.pagination.showingCount,
          {
            count: shownCount,
            total: totalCount,
            type,
          },
        )
      }
    })
  }

  renderFilterDrawerTrigger (document) {
    const existingEl = getElement(
      '[data-collection-filters-el="filter-drawer"]',
    )
    if (!existingEl) return

    const newEl = getElement('[data-collection-filters-el="filter-drawer"]', {
      context: document,
    })
    if (!newEl) return

    existingEl.innerHTML = newEl.innerHTML
  }

  renderFauxFacets (document) {
    const currentFauxFacets = this.element.els['faux-facet-trigger']?.elements
    if (!currentFauxFacets?.length) return

    const newFauxFacets = getElements(
      this.element.els['faux-facet-trigger'].selector,
      { context: document },
    )

    currentFauxFacets.forEach((fauxFacet, index) => {
      const newFauxFacet = newFauxFacets[index]
      if (!newFauxFacet) return

      fauxFacet.innerHTML = newFauxFacet.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends BAOCustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['product-grid', 'facets', 'facet']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()
    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.fallbackCollection) {
      this.replaceComponent()
    }
  }

  setupListeners () {
    super.setupListeners()

    /* Accessed in facet-remove.js */
    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = debounce((e) => this.onSubmit(e), 500)
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))

    this.els?.['faux-facet-trigger']?.elements.forEach((fauxFacetTriggerEl) => {
      this.listeners.add(
        fauxFacetTriggerEl,
        'click',
        this.onFauxFacetClick.bind(this),
      )
    })
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const target = event.target.closest('a')
    const searchParams = new URLSearchParams(target.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    event.preventDefault()

    const parentForm = event.target.closest('form')

    if (document.activeElement && document.activeElement.id) {
      this.dataset.restoreFocusId = document.activeElement.id
    }

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)

    if (
      formData.has('filter.v.price.gte') &&
      formData.has('filter.v.price.lte')
    ) {
      const gte = formData.get('filter.v.price.gte')
      const lte = formData.get('filter.v.price.lte')

      if (gte === '0' && !lte) {
        formData.delete('filter.v.price.gte')
        formData.delete('filter.v.price.lte')
      }
    }

    if (formData.has('sort_by')) {
      const sortBy = formData.get('sort_by')

      if (sortBy === event.target.dataset.defaultSortBy) {
        formData.delete('sort_by')
      }
    }

    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, event })

    const { 'product-grid': productGrid, header } = this.els
    const grid = productGrid.element

    if (grid.getBoundingClientRect().top < 0) {
      grid.style.setProperty(
        '--FilterBar_Height',
        `${header?.element?.offsetHeight ?? 0}px`,
      )

      grid.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const pagination = event.target.closest(this.els?.pagination?.selector)
    if (!pagination) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  onFauxFacetClick (event) {
    const clickedTrigger = event.target.closest(
      '[data-collection-filters-el="faux-facet-trigger"]',
    )

    if (!clickedTrigger) return

    const drawerAccordionTriggerEls = this.querySelectorAll(
      `[data-accordion-group-el="trigger"]`,
    )
    const drawerAccordionContentEls = this.querySelectorAll(
      `[data-accordion-group-el="content"]`,
    )

    drawerAccordionTriggerEls.forEach((triggerEl) => {
      triggerEl.setAttribute(
        'aria-expanded',
        triggerEl.getAttribute('aria-controls') ===
          clickedTrigger.dataset.accordion,
      )
    })

    drawerAccordionContentEls.forEach((contentEl) => {
      contentEl.setAttribute(
        'aria-hidden',
        contentEl.getAttribute('id') !== clickedTrigger.dataset.accordion,
      )
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const pathname = this.collectionUrl
        ? this.collectionUrl
        : window.location.pathname

      const url = `${pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`

      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }

    eventBus.dispatchEvent(EVENTS.COLLECTION.UPDATE, { searchParams })
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    let url = window.location.pathname
    let searchParamsString = ''

    if (searchParams?.toString()) {
      searchParamsString = `?${searchParams?.toString()}`
    }

    url += searchParamsString

    history.pushState({ searchParams: searchParams.toString() }, '', url)
  }

  async replaceComponent () {
    try {
      const response = await this.fetcher.fetch(
        this.dataset.fallbackCollection,
        'template-collection-product-grid',
      )
      const newDOM = sectionRenderer.convertToDOM(response)
      const myself = newDOM.querySelector(this.localName)
      myself.dataset.collectionUrl = this.dataset.fallbackCollection

      this.outerHTML = myself.outerHTML
    } catch (error) {
      console.error('Failed to replace page with fallback collection', error)
    }
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }

  get collectionUrl () {
    return this.dataset.collectionUrl || ''
  }

  get fallbackCollection () {
    return this.dataset.fallbackCollection || ''
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}
